import { JetView } from 'webix-jet';
import { devConfig, profile } from '../../../config/config';
import { flattenPermissions, markPermissionsTree } from 'views/security/perfilopciones/permissionsTree';

/**
 * Class representing a view for filtering user profile options.
 * @extends JetView
 */
export default class FilterPerfilOpcionesView extends JetView {
    /**
     * Configures the UI components of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const filter = {
            margin: 5,
            minHeight: screen.width < 500 ? 150 : 0, // Minimum height based on screen width
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    cols: [
                        {
                            view: 'richselect', // Dropdown for user type selection
                            name: 'TypeUser',
                            id: 'TypeUser',
                            label: _('Tipo de usuario'), // User type label
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [],
                            required: true, // Field is required
                            invalidMessage: _('Campo Requerido'), // Error message for required field
                        },
                        {
                            view: 'combo', // Combo box for username selection
                            name: 'Username',
                            id: 'Username',
                            label: _('Usuario'), // Username label
                            labelPosition: 'top',
                            options: {
                                keyPressTimeout: 1000, // Delay for key press
                                body: {
                                    dataFeed: devConfig.geturlApi() + 'Admin/GetUserFilterSearch', // API endpoint for user search
                                    ready: function () {},
                                },
                            },
                        },
                        {
                            view: 'richselect', // Dropdown for the action type (replaces the old permissions switch)
                            name: 'PermissionsMode',
                            id: 'PermissionsMode',
                            label: _('Tipo de acción'), // Action type label
                            labelPosition: 'top',
                            value: 'Allow', // Default action type: "Autorizaciones"
                            options: [
                                { id: 'Allow', value: _('Autorizaciones') }, // Grants the selected permissions
                                { id: 'Deny', value: _('Bloqueos') }, // Denies the selected permissions
                            ],
                        },
                    ],
                },
            ],
        };
        const upper_section = {
            css: 'father-container',
            rows: [
                {
                    margin: 10,
                    cols: [filter], // Include filter in upper section
                },
            ],
        };
        const accordion_section = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion', // Accordion for collapsible sections
                    css: 'betShop',
                    collapsed: false,
                    paddingY: 10,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Header for filters section
                            id: 'b5',
                            body: {
                                padding: 10,
                                rows: [upper_section], // Only the filter fields (the "Crear Permiso" action moved to the header toolbar)
                            },
                        },
                    ],
                },
            ],
        };
        const steps_tabbar = {
            view: 'tabbar', // Tab bar with the permission assignment steps
            id: 'ScopeTab', // Identifier for the steps tab bar
            height: 43, // Height of the tab bar
            value: 'User', // Default active step
            options: [
                { id: 'User', value: _('Usuario') }, // User step (permissions per user)
                { id: 'Global', value: _('Global') }, // Global step (permissions per role)
                { id: 'Operation', value: _('Operación') }, // Operation step (denials per role)
            ],
        };
        return {
            view: 'form', // Main view is a form
            type: 'clean',
            rows: [steps_tabbar, accordion_section], // Include steps tab bar and accordion section
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule for non-empty fields
            },
        };
    }

    /**
     * Initializes the view and sets up event handlers.
     */
    init() {
        const _ = this.app.getService('locale')._; // Localization service
        const vthis = this; // Reference to the current instance
        var popupAdd; // Variable to hold the popup for adding permissions
        $$('TypeUser').hide(); // Initially hide the user type field (default step is "Usuario")

        /**
         * Renders a permissions tree (already marked) into the dataview and the search combo.
         * @param {Array} treeData - Permissions tree with markCheckbox flags applied.
         */
        const renderPermissionsTree = (treeData) => {
            const flatList = flattenPermissions(treeData); // Build the search list before parsing (parse mutates the tree)
            $$('dataview').clearAll(); // Clear data view
            $$('dataview').parse(treeData); // Populate data view with the permissions tree
            $$('FilterFather').getList().clearAll(); // Clear filter options
            $$('FilterFather').getList().parse(flatList); // Populate filter options
            $$('FilterFather').setValue(''); // Reset filter selection
            $$('b5').collapse(); // Collapse filters section
            $$('dataview').scrollTo(100, 0); // Scroll data view
        };

        /**
         * Requests the permissions tree of GetGroupPermissions2 (new format)
         * and renders it into the dataview and the search combo.
         * @param {string} queryParams - Query string with the scope parameters (without Mode).
         */
        const requestPermissionsTree = (queryParams) => {
            webix
                .ajax()
                .headers({
                    'Content-type': 'application/json',
                })
                .post(
                    devConfig.geturlApi() +
                        'UserManagement/GetGroupPermissions2?' +
                        queryParams +
                        '&Mode=' +
                        $$('PermissionsMode').getValue(), // Action type dropdown already holds the Mode value (Allow | Deny)
                    '',
                    {
                        error: function (text, data, XmlHttpRequest) {
                            webix.message(_('Error! ')); // Error message on failure
                        },
                        success: function (text, data, XmlHttpRequest) {
                            var data = JSON.parse(text); // Parse response data
                            if (!data.HasError && data.Data != undefined) {
                                renderPermissionsTree(markPermissionsTree(data.Data)); // Mark checkState and render the tree
                            } else {
                                webix.message({
                                    type: 'alert-error', // Error message when the response has errors
                                    text:
                                        data.AlertMessage != undefined && data.AlertMessage != ''
                                            ? data.AlertMessage
                                            : _('Error! '), // Show the backend message when available
                                });
                            }
                        },
                    },
                );
        };

        /**
         * Loads the real permissions tree of a specific user ("Usuario" step).
         * Scope=User only expects UserId (roleId is omitted).
         * @param {string|number} userId - Id of the selected user.
         */
        const loadUserPermissions = (userId) => {
            requestPermissionsTree('UserId=' + userId + '&Scope=User');
        };

        /**
         * Loads the real denials tree of a role ("Operación" step).
         * Scope=Operation expects the roleId.
         * @param {string} roleId - Id of the selected role.
         */
        const loadOperationPermissions = (roleId) => {
            requestPermissionsTree('roleId=' + roleId + '&Scope=Operation');
        };

        /**
         * Loads the real permissions tree of a role ("Global" step).
         * Scope=Global expects the roleId.
         * @param {string} roleId - Id of the selected role.
         */
        const loadGlobalPermissions = (roleId) => {
            requestPermissionsTree('roleId=' + roleId + '&Scope=Global');
        };

        /**
         * Applies the visibility and defaults of each step (tab).
         * @param {string} step - Active step id ('User' | 'Global' | 'Operation').
         */
        const applyScopeStep = (step) => {
            if ($$('dataview')) {
                $$('dataview').clearAll(); // Clear permissions list when switching steps
            }
            if ($$('FilterFather')) {
                $$('FilterFather').getList().clearAll(); // Clear search options
                $$('FilterFather').setValue(''); // Reset search selection
            }
            if (step == 'User') {
                $$('TypeUser').hide(); // User step works with a specific user
                $$('Username').show(); // Show the user search field
                setPermissionsModeSilent('Allow'); // Default action type: "Autorizaciones"
                if ($$('Username').getValue() != '') {
                    loadUserPermissions($$('Username').getValue()); // Reload user permissions if a user is already selected
                }
            } else {
                if (step == 'Global') {
                    $$('TypeUser').show(); // Global step works with roles
                    $$('Username').hide(); // Username only applies to the "Usuario" step
                    setPermissionsModeSilent('Allow'); // Default action type: "Autorizaciones"
                    if ($$('TypeUser').getValue() != '') {
                        loadGlobalPermissions($$('TypeUser').getValue()); // Reload role permissions if a role is already selected
                    }
                } else {
                    if (step == 'Operation') {
                        $$('TypeUser').show(); // Operation step also works with roles
                        $$('Username').hide(); // Hide the user search field
                        setPermissionsModeSilent('Deny'); // Default action type inverted: "Bloqueos"
                        if ($$('TypeUser').getValue() != '') {
                            loadOperationPermissions($$('TypeUser').getValue()); // Reload denials if a role is already selected
                        }
                    }
                }
            }
        };

        /**
         * Sets the action type dropdown value without triggering its onChange event
         * (avoids duplicated data reloads when switching steps).
         * @param {string} value - 'Allow' for "Autorizaciones", 'Deny' for "Bloqueos".
         */
        const setPermissionsModeSilent = (value) => {
            if ($$('PermissionsMode')) {
                $$('PermissionsMode').blockEvent(); // Block events to avoid reloading twice
                $$('PermissionsMode').setValue(value); // Set the default side of the step
                $$('PermissionsMode').unblockEvent(); // Restore events
            }
        };

        // Event handler for changes in the action type dropdown
        $$('PermissionsMode').attachEvent('onChange', function (newv, oldv) {
            var step = $$('ScopeTab').getValue(); // Active step
            if (step == 'User' && $$('Username').getValue() != '') {
                loadUserPermissions($$('Username').getValue()); // Reload user permissions with the new mode
            } else {
                if (step == 'Operation' && $$('TypeUser').getValue() != '') {
                    loadOperationPermissions($$('TypeUser').getValue()); // Reload denials with the new mode
                } else {
                    if (step == 'Global' && $$('TypeUser').getValue() != '') {
                        loadGlobalPermissions($$('TypeUser').getValue()); // Reload role permissions with the new mode
                    }
                }
            }
        });

        // Event handler for step (tab) changes
        $$('ScopeTab').attachEvent('onChange', function (newv, oldv) {
            applyScopeStep(newv); // Apply visibility and defaults of the selected step
        });

        // Fetch user types from the API
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'AdminUser/GetTypeUser', '', {
                error: function (text, data, XmlHttpRequest) {
                    webix.message('Error! '); // Error message on failure
                },
                success: function (text, data, XmlHttpRequest) {
                    var data = JSON.parse(text); // Parse response data
                    var optionsTypeUser = $$('TypeUser').getList(); // Get the list of user types
                    optionsTypeUser.clearAll(); // Clear existing options
                    optionsTypeUser.parse(data.Data); // Populate with new data
                },
            });

        // Event handler for changes in user type selection
        $$('TypeUser').attachEvent('onChange', function (newv, oldv) {
            if ($$('ScopeTab').getValue() == 'Operation') {
                // Operation step: loads the real denials of the selected role
                if (newv != '' && newv != undefined) {
                    loadOperationPermissions(newv);
                }
                return;
            }
            // Global step: loads the real permissions of the role (new GetGroupPermissions2 format)
            if (newv != '' && newv != undefined) {
                loadGlobalPermissions(newv);
            }
        });

        // Event handler for changes in username selection ("Usuario" step)
        $$('Username').attachEvent('onChange', function (newv, oldv) {
            if ($$('ScopeTab').getValue() == 'User' && newv != '' && newv != undefined) {
                loadUserPermissions(newv); // Load the real permissions of the selected user
            }
        });

        // Event handler for creating a permission
        this.on(this.app, 'createPermission:button', (filter) => {
            webix
                .ajax()
                .headers({
                    'Content-type': 'application/json',
                })
                .post(devConfig.geturlApi() + 'UserManagement/GetAllMenus', '', {
                    error: function (text, data, XmlHttpRequest) {
                        webix.message(_('Error! ')); // Error message on failure
                    },
                    success: function (text, data, XmlHttpRequest) {
                        var data = JSON.parse(text); // Parse response data
                        if (!data.HasError) {
                            data.Data.forEach((item) => {
                                item.value = item.description; // Set display value for menu items
                                if (item.data != undefined) {
                                    item.data.forEach((item2) => {
                                        item2.value = item2.description; // Set display value for submenu items
                                    });
                                }
                            });
                            if (data.Data.length > 0) {
                                popupAdd = webix
                                    .ui({
                                        view: 'window', // Popup window for creating permission
                                        id: 'CreatePermission',
                                        fullscreen: screen.width < 500 ? true : false,
                                        head: {
                                            view: 'toolbar',
                                            cols: [
                                                {
                                                    view: 'label',
                                                    label: _(
                                                        'Crear Permiso', // Label for the popup
                                                    ),
                                                },
                                                {
                                                    view: 'icon',
                                                    icon: 'wxi-close',
                                                    click: function () {
                                                        this.getTopParentView().hide(); // Close popup on icon click
                                                    },
                                                },
                                            ],
                                        },
                                        position: 'center',
                                        modal: true,
                                        body: {
                                            view: 'form', // Form inside the popup
                                            id: 'createPermissionForm',
                                            elements: [
                                                {
                                                    margin: 10,
                                                    rows: [
                                                        {
                                                            view: 'text', // Input for URL
                                                            labelPosition: 'top',
                                                            label: _('Url'), // URL label
                                                            name: 'Url',
                                                            required: true, // Field is required
                                                        },
                                                        {
                                                            view: 'text', // Input for description
                                                            labelPosition: 'top',
                                                            label: _(
                                                                'Descripción', // Description label
                                                            ),
                                                            name: 'Description',
                                                            required: true, // Field is required
                                                        },
                                                        {
                                                            view: 'combo', // Combo for parent menu selection
                                                            labelPosition: 'top',
                                                            label: _(
                                                                'Menú Padre', // Parent menu label
                                                            ),
                                                            name: 'FatherMenu',
                                                            options: data.Data, // Options from API response
                                                            required: false,
                                                            on: {
                                                                onChange: function () {
                                                                    let currentValue = this.getValue(); // Get selected value
                                                                    let subMenus = []; // Array for submenus
                                                                    if (
                                                                        data.Data.filter((item) => {
                                                                            return item.id == currentValue;
                                                                        }) != undefined
                                                                    ) {
                                                                        subMenus = data.Data.filter((item) => {
                                                                            return item.id == currentValue;
                                                                        })[0].data; // Get submenus for selected parent
                                                                    }
                                                                    $$('SubMenu').define(
                                                                        'options',
                                                                        subMenus, // Set submenu options
                                                                    );
                                                                    $$('SubMenu').refresh(); // Refresh submenu
                                                                },
                                                            },
                                                        },
                                                        {
                                                            view: 'combo', // Combo for submenu selection
                                                            labelPosition: 'top',
                                                            label: _(
                                                                'Sub Menú', // Submenu label
                                                            ),
                                                            name: 'SubMenu',
                                                            id: 'SubMenu',
                                                            options: [],
                                                            required: false,
                                                        },
                                                    ],
                                                },
                                                {
                                                    cols: [
                                                        {
                                                            view: 'button', // Cancel button
                                                            value: _(
                                                                'Cancelar', // Cancel label
                                                            ),
                                                            click: function () {
                                                                this.getTopParentView().hide(); // Close popup on cancel
                                                                $$('createPermissionForm').setValues(
                                                                    $$('createPermissionForm').getCleanValues(), // Reset form values
                                                                );
                                                            },
                                                        },
                                                        {
                                                            view: 'button', // Save button
                                                            type: 'form',
                                                            value: _(
                                                                'Guardar', // Save label
                                                            ),
                                                            click: function ($e) {
                                                                var values = $$('createPermissionForm').getValues(); // Get form values
                                                                if ($$('createPermissionForm').validate()) {
                                                                    webix
                                                                        .ajax()
                                                                        .headers({
                                                                            'Content-type': 'application/json',
                                                                        })
                                                                        .post(
                                                                            devConfig.geturlApi() +
                                                                                'UserManagement/CreatePermission',
                                                                            values, // Send form values to API
                                                                            {
                                                                                error: function (
                                                                                    text,
                                                                                    data,
                                                                                    XmlHttpRequest,
                                                                                ) {
                                                                                    webix.message('Error! '); // Error message on failure
                                                                                },
                                                                                success: function (
                                                                                    text,
                                                                                    data,
                                                                                    XmlHttpRequest,
                                                                                ) {
                                                                                    text = JSON.parse(text); // Parse response data
                                                                                    if (text.HasError) {
                                                                                        webix.message(
                                                                                            'Error: ' +
                                                                                                text.AlertMessage, // Show error message if any
                                                                                        );
                                                                                    } else {
                                                                                        webix.message(
                                                                                            'Permiso creado satisfactoriamente', // Success message
                                                                                        );
                                                                                    }
                                                                                },
                                                                            },
                                                                        );
                                                                    this.getTopParentView().hide(); // Close popup after saving
                                                                } else {
                                                                    webix.message({
                                                                        title: _(
                                                                            '¡Error!', // Error title
                                                                        ),
                                                                        type: 'alert-error',
                                                                        text: _(
                                                                            'Completa los campos...', // Error message for validation
                                                                        ),
                                                                    });
                                                                }
                                                            },
                                                        },
                                                    ],
                                                },
                                            ],
                                            elementsConfig: {
                                                on: {
                                                    onChange: function () {
                                                        this.validate(); // Validate form on change
                                                    },
                                                },
                                            },
                                        },
                                    })
                                    .show(); // Show the popup
                                $$('createPermissionForm').clearValidation(); // Clear validation state
                            }
                        }
                    },
                });
        });
    }
}

// FILE DOCUMENTED
